import {
  Component,
  EventEmitter,
  Input,
  OnInit,
  Output,
  Signal,
  WritableSignal,
  computed,
  inject,
  signal,
} from '@angular/core';
import { HttpClient } from '@angular/common/http';
// Debe ser el paquete completo, no 'echarts/core': ngx-echarts carga 'echarts'
// y registerMap escribe en el registro de ese modulo. Importar el core deja el
// mapa registrado en otra instancia y la serie truena con "Map mexico not exists".
import * as echarts from 'echarts';
import { EChartsOption } from 'echarts';
import { ChartService } from '../../services/chart.service';
import { ThemeService } from '../../services/theme.service';
import { ViewportService } from '../../services/viewport.service';

/**
 * Coropleta de Mexico. Carga el GeoJSON de assets, lo registra en ECharts
 * y arma la serie con el NPS por estado.
 */
@Component({
  selector: 'app-map-chart',
  templateUrl: './map-chart.component.html',
  styleUrl: './map-chart.component.scss',
  standalone: false,
})
export class MapChartComponent implements OnInit {

  private static readonly MAP_NAME = 'mexico';
  private static registered = false;

  private readonly chartService = inject(ChartService);
  private readonly http = inject(HttpClient);
  private readonly themeService = inject(ThemeService);
  private readonly viewport: ViewportService = inject(ViewportService);

  /** El GeoJSON ya esta registrado y la serie se puede pintar. */
  private readonly ready = signal(MapChartComponent.registered);

  /** NPS por estado tal como lo entrego la pagina. */
  private readonly source = signal<Array<{ name: string; value: number }>>([]);

  /** Altura de escritorio declarada por la pagina, sin traducir. */
  private readonly baseHeight: WritableSignal<number> = signal<number>(620);

  /** Señal, no campo: la pagina puede reasignar el arreglo al filtrar. */
  @Input()
  public set data(value: Array<{ name: string; value: number }>) {
    this.source.set(value ?? []);
  }

  /**
   * La firma publica no cambia: la pagina sigue pasando 620 y es el componente
   * quien evita la tira vertical en un telefono.
   */
  @Input()
  public set height(value: number) {
    this.baseHeight.set(value);
  }

  /**
   * Instancia de ECharts ya montada.
   *
   * Se saca fuera porque el PNG del mapa sólo lo puede dar el propio lienzo
   * (`getDataURL()`), y el botón que lo descarga vive en la barra de la página,
   * no aquí dentro.
   */
  @Output() public readonly chartReady = new EventEmitter<any>();

  /** Se rearma al cambiar el tema: el cromo del mapa sale de los tokens. */
  public readonly options = computed<EChartsOption | undefined>(() => {
    this.themeService.theme();

    return this.ready()
      ? this.chartService.mexicoMap(MapChartComponent.MAP_NAME, this.source())
      : undefined;
  });

  /**
   * Altura real del lienzo. La usan por igual el mapa y su hueco de carga: si
   * sólo escalara uno, la coropleta daría un salto al terminar de cargar.
   */
  public readonly scaledHeight: Signal<number> = computed<number>(() => {
    // `scaleHeight` ya lee el corte por dentro; leerlo aqui deja la dependencia
    // a la vista para que un refactor del servicio no la rompa en silencio.
    this.viewport.breakpoint();

    return this.viewport.scaleHeight(this.baseHeight());
  });

  public ngOnInit(): void {
    if (MapChartComponent.registered) {
      this.ready.set(true);
      return;
    }

    this.http.get('assets/geo/mexico.geo.json').subscribe({
      next: (geoJson) => {
        echarts.registerMap(MapChartComponent.MAP_NAME, geoJson as any);
        MapChartComponent.registered = true;
        this.ready.set(true);
      },
      error: (error) => console.error('No se pudo cargar el mapa de México', error),
    });
  }
}
